import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchBlogPosts, fetchTaxonomyTerms } from '../lib/cms';
import { getOptimizedImageUrl } from '../lib/utils';
import ScrollReveal from '../components/ScrollReveal';
import ShareButtons from '../components/ShareButtons';
import MediaEmbed from '../components/MediaEmbed';

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');

  const activeCategory = searchParams.get('kategori') || 'alle';

  useEffect(() => {
    async function loadBlogData() {
      setLoading(true);
      try {
        const [postsRes, termsRes] = await Promise.all([
          fetchBlogPosts('sort=date&order=desc'),
          fetchTaxonomyTerms('kategori'),
        ]);

        setPosts(postsRes.items || []);
        setCategories(termsRes || []);
      } catch (err) {
        console.error('Fejl ved indlæsning af blog:', err);
      } finally {
        setLoading(false);
      }
    }
    loadBlogData();
  }, []);

  const handleCategoryChange = (catSlug) => {
    if (catSlug === 'alle') {
      searchParams.delete('kategori');
      setSearchParams(searchParams);
    } else {
      setSearchParams({ kategori: catSlug });
    }
  };

  // Filter posts by category and search query
  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      // Category filter
      if (activeCategory !== 'alle') {
        const postCategories = post.terms?.kategori || [];
        if (!postCategories.includes(activeCategory)) {
          return false;
        }
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const title = (post.title?.da || post.title || '').toLowerCase();
        const summary = (post.summary?.da || post.summary || '').toLowerCase();
        const content = (post.content?.da || post.content || '').toLowerCase();
        if (!title.includes(q) && !summary.includes(q) && !content.includes(q)) {
          return false;
        }
      }

      return true;
    });
  }, [posts, activeCategory, searchQuery]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('da-DK', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(date);
    } catch {
      return dateString;
    }
  };

  // Helper to extract image URL
  const getPostImage = (post) => {
    if (post.image && post.image.length > 0) return post.image[0];
    if (post.gallery && post.gallery.length > 0) return post.gallery[0];
    return 'https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/billede.jpg';
  };

  const getPostSlug = (post) => {
    return post.slug?.da || post.slug || post.id;
  };

  // Featured post (latest one when no specific search/filter is active)
  const isDefaultView = activeCategory === 'alle' && !searchQuery.trim();
  const featuredPost = isDefaultView && filteredPosts.length > 0 ? filteredPosts[0] : null;
  const remainingPosts = featuredPost ? filteredPosts.slice(1) : filteredPosts;

  return (
    <div className="bg-sand-50 text-foreground min-h-screen">
      {/* Header Banner */}
      <section className="relative py-20 md:py-28 bg-[#60b6c0] text-sand-50 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/sangundervisning-live.webp"
            alt=""
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center flex flex-col gap-4">
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold tracking-wide leading-tight">
            Blog
          </h1>
          <p className="max-w-2xl mx-auto text-sm sm:text-base text-white leading-relaxed">
            Fortællinger, sangtekniske råd og personlige refleksioner om musik, krop og nærvær fra Heidi Svelmøe.
          </p>
        </div>
      </section>

      {/* Filter and Search Bar */}
      <section className="bg-white border-b border-sand-200 py-4">
        <div className="mx-auto max-w-7xl px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto w-full sm:w-auto pb-2 sm:pb-0">
            <button
              onClick={() => handleCategoryChange('alle')}
              className={`px-4 py-2 text-xs font-semibold tracking-wider uppercase transition-colors ${
                activeCategory === 'alle'
                  ? 'bg-clay-700 text-sand-50'
                  : 'bg-sand-100 text-foreground/70 hover:bg-sand-200'
              }`}
            >
              Alle ({posts.length})
            </button>
            {categories.map((cat) => {
              const count = posts.filter((p) => p.terms?.kategori?.includes(cat.slug)).length;
              const isActive = activeCategory === cat.slug;
              return (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 text-xs font-semibold tracking-wider uppercase transition-colors flex items-center gap-1.5 ${
                    isActive
                      ? 'bg-clay-700 text-sand-50'
                      : 'bg-sand-100 text-foreground/70 hover:bg-sand-200'
                  }`}
                >
                  <span>{cat.label}</span>
                  <span className={`text-[10px] ${isActive ? 'text-white/80' : 'text-foreground/50'}`}>
                    ({count})
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search Input */}
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søg i artikler..."
              className="w-full pl-9 pr-4 py-2 bg-sand-50 border border-sand-200 text-xs text-foreground placeholder:text-foreground/50 focus:outline-none focus:border-clay-600"
            />
            <i
              className="fa-regular fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40 text-xs"
              aria-hidden="true"
            />
          </div>
        </div>
      </section>

      {/* 3. MAIN EDITORIAL CONTENT CONTAINER */}
      <section className="py-12 md:py-20">
        <div className="mx-auto max-w-7xl px-6">
          {loading ? (
            <div className="flex min-h-[45vh] items-center justify-center">
              <div className="flex flex-col items-center gap-4">
                <div className="h-12 w-12 animate-spin rounded-full border-3 border-clay-600 border-t-transparent" />
                <span className="text-xs uppercase tracking-widest text-foreground/60 font-medium">
                  Indlæser bloggen...
                </span>
              </div>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-sand-200/90 shadow-sm max-w-xl mx-auto px-8">
              <div className="w-16 h-16 rounded-full bg-sand-100 flex items-center justify-center mx-auto mb-4 text-clay-700">
                <i className="fa-regular fa-compass-drafting text-2xl" aria-hidden="true" />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-stone-900 mb-2">
                Ingen artikler fundet
              </h3>
              <p className="text-foreground/70 text-sm mb-6 leading-relaxed">
                Der er desværre ingen artikler, der matcher din søgning eller den valgte kategori.
              </p>
              <button
                onClick={() => {
                  handleCategoryChange('alle');
                  setSearchQuery('');
                }}
                className="px-6 py-2.5 rounded-full bg-clay-800 text-white text-xs uppercase tracking-widest font-semibold hover:bg-clay-900 transition-all shadow-xs"
              >
                Vis alle artikler
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-14 md:gap-20">
              {/* FEATURED POST (MAGAZINE COVER STORY HERO) */}
              {featuredPost && (
                <ScrollReveal>
                  <div className="relative group rounded-3xl bg-white border border-sand-200/90 overflow-hidden shadow-[0_15px_40px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] transition-all duration-500 grid grid-cols-1 lg:grid-cols-12 items-center">
                    {/* Left: Magazine Feature Media (Video or Image) */}
                    <div className="lg:col-span-5 relative p-6 sm:p-8 flex items-center justify-center bg-sand-100/50">
                      {featuredPost.video_url || featuredPost.video_file ? (
                        <div className="w-full max-w-[300px] sm:max-w-[320px] mx-auto">
                          <MediaEmbed
                            videoUrl={featuredPost.video_url}
                            videoFile={featuredPost.video_file}
                            title={featuredPost.title?.da || featuredPost.title}
                          />
                        </div>
                      ) : (
                        <Link to={`/blog/${getPostSlug(featuredPost)}`} className="block w-full h-full min-h-[340px] sm:min-h-[420px] lg:min-h-[520px]">
                          <img
                            src={getOptimizedImageUrl(featuredPost.image[0], { width: 1000, height: 900, quality: 85 })}
                            alt={featuredPost.title?.da || featuredPost.title}
                            className="w-full h-full object-cover object-top group-hover:scale-103 transition-transform duration-700 ease-out"
                          />
                        </Link>
                      )}

                      {/* Floating Badges */}
                      <div className="absolute top-5 left-5 flex flex-wrap gap-2 pointer-events-none">
                        <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-clay-800/95 backdrop-blur-md text-white text-[11px] font-bold uppercase tracking-wider shadow-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-sand-300 animate-ping" />
                          Nyeste
                        </span>
                        {(featuredPost.video_url || featuredPost.video_file) && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/80 backdrop-blur-md text-white text-[11px] font-semibold shadow-sm">
                            <i className="fa-solid fa-play text-[9px] text-red-400" aria-hidden="true" />
                            <span>Video</span>
                          </span>
                        )}
                        {featuredPost.gallery && featuredPost.gallery.length > 0 && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md text-white text-[11px] font-medium shadow-sm">
                            <i className="fa-solid fa-images text-[10px]" aria-hidden="true" />
                            <span>+{featuredPost.gallery.length} foto</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right: Editorial Typography (7 Cols) */}
                    <div className="lg:col-span-7 p-8 sm:p-10 lg:p-12 flex flex-col justify-between bg-gradient-to-br from-white via-white to-sand-50/50">
                      <div>
                        {/* Meta Category & Date */}
                        <div className="flex flex-wrap items-center gap-2.5 text-xs text-foreground/60 mb-4">
                          {featuredPost.terms?.kategori && featuredPost.terms.kategori.length > 0 && (
                            <span className="px-2.5 py-0.5 rounded-full bg-sand-100 text-clay-900 font-bold uppercase tracking-widest text-[10px]">
                              {featuredPost.terms.kategori[0].replace('-', ' ')}
                            </span>
                          )}
                          <span className="font-medium text-foreground/50">
                            {formatDate(featuredPost.date)}
                          </span>
                        </div>

                        {/* Title */}
                        <h2 className="font-serif text-2xl sm:text-3xl lg:text-[34px] font-bold tracking-tight text-stone-900 mb-4 leading-snug group-hover:text-clay-700 transition-colors">
                          <Link to={`/blog/${getPostSlug(featuredPost)}`}>
                            {featuredPost.title?.da || featuredPost.title}
                          </Link>
                        </h2>

                        {/* Excerpt */}
                        <p className="text-stone-700 text-sm sm:text-base leading-relaxed line-clamp-4 mb-6 font-normal">
                          {featuredPost.summary?.da || featuredPost.summary}
                        </p>
                      </div>

                      {/* Author row & Direct CTA */}
                      <div className="pt-6 border-t border-sand-200/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/heidi-svelmoe-portraet.webp', { width: 90, height: 90, quality: 85 })}
                            alt="Heidi Svelmøe"
                            className="w-10 h-10 rounded-full object-cover border border-sand-200 shadow-2xs"
                          />
                          <div>
                            <span className="text-xs font-bold text-stone-900 block">Heidi Svelmøe</span>
                            <span className="text-[11px] text-foreground/55 block">Sanger & Underviser</span>
                          </div>
                        </div>

                        <Link
                          to={`/blog/${getPostSlug(featuredPost)}`}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-clay-800 text-white text-xs uppercase tracking-widest font-bold hover:bg-clay-900 transition-all shadow-sm group/btn"
                        >
                          <span>Læs artiklen</span>
                          <i className="fa-solid fa-arrow-right text-[11px] group-hover/btn:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              )}

              {/* REMAINING POSTS (MAGAZINE GRID) */}
              {remainingPosts.length > 0 && (
                <div>
                <div className="flex items-center justify-between mb-8 pb-3 border-b border-sand-200/80">
                  <div className="flex items-center gap-3">
                    <span className="w-2.5 h-2.5 rounded-full bg-clay-700" />
                    <h3 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight text-stone-900">
                      {isDefaultView ? 'Flere artikler & historier' : 'Fundne artikler'}
                    </h3>
                  </div>
                  <span className="text-xs text-foreground/50 font-medium uppercase tracking-wider">
                    {remainingPosts.length} {remainingPosts.length === 1 ? 'artikel' : 'artikler'}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
                  {remainingPosts.map((post) => {
                    const slug = getPostSlug(post);
                    const title = post.title?.da || post.title;
                    const summary = post.summary?.da || post.summary;
                    const imageUrl = getPostImage(post);
                    const hasVideo = Boolean(post.video_url || post.video_file);
                    const hasGallery = post.gallery && post.gallery.length > 0;
                    const categoryTag = post.terms?.kategori?.[0] || 'inspiration';

                    return (
                      <ScrollReveal key={post.id}>
                        <article className="h-full bg-white rounded-2xl border border-sand-200/90 overflow-hidden shadow-[0_6px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_15px_35px_rgba(0,0,0,0.08)] transition-all duration-300 flex flex-col group">
                          {/* Image Box */}
                          <div className="relative aspect-[16/10] overflow-hidden bg-sand-100">
                            <Link to={`/blog/${slug}`} className="block w-full h-full">
                              <img
                                src={getOptimizedImageUrl(imageUrl, { width: 750, quality: 85 })}
                                alt={title}
                                className="w-full h-full object-cover object-top group-hover:scale-106 transition-transform duration-600 ease-out"
                              />
                            </Link>

                            {/* Floating category tag */}
                            <div className="absolute top-3.5 left-3.5 flex flex-wrap gap-1.5">
                              <span className="px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-md text-stone-900 text-[10px] font-bold uppercase tracking-wider shadow-xs">
                                {categoryTag.replace('-', ' ')}
                              </span>
                            </div>

                            {/* Media indicators */}
                            <div className="absolute top-3.5 right-3.5 flex items-center gap-1.5">
                              {hasVideo && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/75 backdrop-blur-xs text-white text-[10px] font-semibold">
                                  <i className="fa-solid fa-play text-[8px] text-red-400" />
                                  <span>Video</span>
                                </span>
                              )}
                              {hasGallery && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/70 backdrop-blur-xs text-white text-[10px] font-medium">
                                  <i className="fa-solid fa-images text-[9px]" />
                                  <span>+{post.gallery.length}</span>
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Text Body */}
                          <div className="p-6 sm:p-7 flex-grow flex flex-col justify-between">
                            <div>
                              {/* Date */}
                              <div className="flex items-center gap-2 text-xs text-foreground/50 mb-3">
                                <span>{formatDate(post.date)}</span>
                              </div>

                              {/* Heading */}
                              <h4 className="font-serif text-xl sm:text-2xl font-bold text-stone-900 mb-3 leading-snug group-hover:text-clay-700 transition-colors">
                                <Link to={`/blog/${slug}`}>{title}</Link>
                              </h4>

                              {/* Summary */}
                              <p className="text-stone-700 text-sm leading-relaxed line-clamp-3 font-normal">
                                {summary}
                              </p>
                            </div>

                            {/* Card Footer */}
                            <div className="mt-6 pt-4 border-t border-sand-200/80 flex items-center justify-between">
                              <Link
                                to={`/blog/${slug}`}
                                className="text-xs uppercase tracking-wider font-bold text-clay-800 hover:text-black transition-colors inline-flex items-center gap-1.5 group/link"
                              >
                                <span>Læs artikel</span>
                                <i className="fa-solid fa-arrow-right text-[10px] group-hover/link:translate-x-1 transition-transform" />
                              </Link>

                              <ShareButtons
                                compact
                                url={typeof window !== 'undefined' ? `${window.location.origin}/blog/${slug}` : ''}
                                title={title}
                                summary={summary}
                                image={imageUrl}
                              />
                            </div>
                          </div>
                        </article>
                      </ScrollReveal>
                    );
                  })}
                </div>
              </div>
            )}
            </div>
          )}
        </div>
      </section>

      {/* 4. INSPIRING EDITORIAL CALLOUT SECTION */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#FAF8F5] to-sand-100/70 border-t border-sand-200/90">
        <div className="mx-auto max-w-5xl px-6">
          <div className="relative rounded-3xl bg-white border border-sand-200/90 p-8 sm:p-12 md:p-16 shadow-sm overflow-hidden text-center flex flex-col items-center">
            {/* Background subtle ornament */}
            <div className="absolute -right-16 -bottom-16 w-64 h-64 rounded-full bg-[#60b6c0]/10 blur-2xl pointer-events-none" />
            <div className="absolute -left-16 -top-16 w-64 h-64 rounded-full bg-clay-400/10 blur-2xl pointer-events-none" />

            <span className="text-xs uppercase tracking-[0.25em] text-clay-700 font-bold mb-3">
              Kreativitet, Sang & Nærvær
            </span>

            <h3 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-stone-900 max-w-2xl leading-tight">
              Drømmer du om at styrke din egen stemme?
            </h3>

            <p className="mt-4 max-w-xl text-sm sm:text-base text-foreground/75 leading-relaxed font-light">
              Uanset om du er begynder eller erfaren sanger, kan du booke en individuel time i Solsalen i Odense. Her arbejder vi med kropskontakt, fri klang og autentisk udtryk.
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/kontakt"
                className="px-7 py-3 rounded-full bg-clay-800 text-white text-xs uppercase tracking-widest font-bold hover:bg-clay-900 transition-all shadow-sm"
              >
                Book sangundervisning
              </Link>
              <Link
                to="/undervisning"
                className="px-7 py-3 rounded-full bg-white text-stone-800 border border-sand-300 text-xs uppercase tracking-widest font-bold hover:bg-sand-50 transition-all shadow-2xs"
              >
                Læs om undervisningen
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
